import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Pagination,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";

import { EmptyMessage, ManagementLayout } from "@components/shared";
import OrganizationMemberForm from "./OrganizationMemberForm";
import {
  ORGANIZATION_MEMBER_CONFIG,
  fetchMembers,
  membersQueryKey,
  orgJoinDisplayName,
  orgJoinId,
  orgJoinPersonFields,
} from "./organizationMemberShared";

const PAGE_SIZE = 25;

export default function OrganizationMemberManagement({
  organizationId,
  type = "users",
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const config = ORGANIZATION_MEMBER_CONFIG[type];

  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    setSearch("");
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [organizationId, type]);

  const membersKey = membersQueryKey(organizationId, type);

  const membersQuery = useQuery({
    queryKey: [
      ...membersKey,
      pagination.pageIndex,
      pagination.pageSize,
      search,
    ],
    queryFn: ({ signal }) =>
      fetchMembers({
        organizationId,
        type,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        searchValue: search,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  const invalidateMembers = () => {
    queryClient.invalidateQueries({ queryKey: membersKey });
    queryClient.invalidateQueries({
      queryKey: [...membersQueryKey(organizationId, "users"), "options"],
    });
  };

  const removeMemberMutation = useMutation({
    mutationFn: (joinId) => axios.delete(config.remove(organizationId, joinId)),
    onSuccess: () => {
      toast.success(t("updated"));
      invalidateMembers();
    },
    onError: (err) => toast.error(err),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (row) => {
      const joinId = orgJoinId(row, type);
      const isActive = row.status === 2;
      const url = isActive
        ? config.inactivate(organizationId, joinId)
        : config.activate(organizationId, joinId);
      return axios.post(url, {});
    },
    onSuccess: () => {
      toast.success(t("updated"));
      invalidateMembers();
    },
    onError: (err) => toast.error(err),
  });

  const members = membersQuery.data?.data || [];
  const pageCount = membersQuery.data?.page_count || 0;
  const total = membersQuery.data?.total ?? members.length;

  const table = useReactTable({
    data: members,
    columns: useMemo(
      () => [
        {
          id: "member",
          accessorFn: (row) => orgJoinDisplayName(row) || row.user_id,
        },
      ],
      []
    ),
    pageCount,
    manualPagination: true,
    state: {
      pagination,
    },
    onPaginationChange: (updater) => {
      setPagination((current) =>
        typeof updater === "function" ? updater(current) : updater
      );
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;
  const isInitialLoading = membersQuery.isLoading && !membersQuery.data;
  const isFetching = membersQuery.isFetching;

  if (isInitialLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (membersQuery.isError && !membersQuery.data) {
    return (
      <EmptyMessage
        title={t("no_results")}
        text={String(membersQuery.error || t("no_results"))}
      />
    );
  }

  const list = (
    <Stack gap="md">
      <Title order={5}>{t(config.titleKey)}</Title>
      <TextInput
        placeholder={t("search")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPagination((current) => ({ ...current, pageIndex: 0 }));
        }}
      />

      <Stack gap="xs">
        {!rows.length ? (
          <EmptyMessage
            title={search.trim() ? t("no_results") : t("no_members_yet")}
            text={
              search.trim()
                ? t("if_you_had_results_they_d_appear_here")
                : t("no_members_yet_description")
            }
          />
        ) : (
          rows.map((row) => {
            const member = row.original;
            const joinId = orgJoinId(member, type);
            const name = orgJoinDisplayName(member);
            const { email } = orgJoinPersonFields(member);
            const busy =
              removeMemberMutation.isPending &&
              removeMemberMutation.variables === joinId;
            const statusBusy =
              toggleStatusMutation.isPending &&
              orgJoinId(toggleStatusMutation.variables, type) === joinId;

            return (
              <Card key={joinId || member.user_id}>
                <Flex gap="sm" align="center">
                  <div style={{ flexGrow: 1 }}>
                    <Text
                      fw={700}
                      component={Link}
                      to={
                        type === "users"
                          ? `/users/${member.user_id}`
                          : `/organizations/${organizationId}/users/${member.user_id}`
                      }
                      c="dark"
                    >
                      {name || member.user_id}
                    </Text>
                    {email && (
                      <Text size="sm" c="dimmed">
                        {email}
                      </Text>
                    )}
                    {member.status_formatted && (
                      <Badge size="xs" variant="light" mt={4}>
                        {member.status_formatted}
                      </Badge>
                    )}
                    {type === "users" && (
                      <Group gap={4} mt={4}>
                        {member.is_org_admin && (
                          <Badge size="xs" variant="outline">
                            {t("organization_admin_plural")}
                          </Badge>
                        )}
                        {member.is_org_manager && (
                          <Badge size="xs" variant="outline">
                            {t("manager_plural")}
                          </Badge>
                        )}
                      </Group>
                    )}
                  </div>
                  <Flex gap="xs">
                    {type === "users" && (
                      <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        loading={statusBusy}
                        onClick={() => toggleStatusMutation.mutate(member)}
                      >
                        {member.status === 2 ? t("inactive") : t("active")}
                      </Button>
                    )}
                    {type !== "users" && (
                      <ActionIcon
                        size="sm"
                        radius="xl"
                        variant="light"
                        color="red"
                        title={t("remove")}
                        loading={busy}
                        onClick={() => removeMemberMutation.mutate(joinId)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Flex>
                </Flex>
              </Card>
            );
          })
        )}
      </Stack>

      <Group mt="sm" align="center">
        <Pagination
          total={Math.max(pageCount, 1)}
          value={pagination.pageIndex + 1}
          onChange={(page) => table.setPageIndex(page - 1)}
          size="sm"
          disabled={isFetching || pageCount <= 1}
        />
        <Text size="sm">
          {total} {t("total").toLowerCase()}
        </Text>
        {isFetching && <Loader size="xs" />}
      </Group>
    </Stack>
  );

  return (
    <ManagementLayout
      dataComponent={list}
      createComponent={
        <OrganizationMemberForm
          organizationId={organizationId}
          type={type}
          onSuccess={invalidateMembers}
        />
      }
    />
  );
}
